import { useEffect, useState } from "react";
import { Star, ArrowUpRight, LockSimple, Warning, HandCoins } from "@phosphor-icons/react";
import { Reveal, Eyebrow } from "./Reveal";
import { api, scrollToId } from "../lib/api";

function Stars({ rating }) {
  return (
    <span className="flex items-center gap-0.5" aria-label={`Rated ${rating} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={15}
          weight={i < Math.round(rating) ? "fill" : "regular"}
          className={i < Math.round(rating) ? "text-accent" : "text-border"}
        />
      ))}
    </span>
  );
}

export default function Directory() {
  const [companies, setCompanies] = useState(null);

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => setCompanies([]));
  }, []);

  return (
    <section id="companies" data-testid="companies-section" className="py-24 md:py-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <Eyebrow>The Directory</Eyebrow>
            <h2 className="font-serif text-primary tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.08]" data-testid="companies-heading">
              Who to engage —<br />and who to walk away from
            </h2>
          </div>
          <p className="text-foreground/60 max-w-sm text-sm md:text-base md:text-right">
            Real companies, real public ratings — BBB, Trustpilot and complaint records, already researched for you. The names are reserved for members.
          </p>
        </Reveal>

        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="companies-grid">
          {companies === null && (
            <p className="text-sm text-foreground/50 col-span-full" data-testid="companies-loading">Loading the directory…</p>
          )}
          {companies && companies.map((c, i) => (
            <Reveal key={c.id} delay={(i % 3) * 0.1}>
              <div
                data-testid={`company-card-${i}`}
                className="group h-full bg-card border border-border rounded-sm p-8 flex flex-col hover:border-accent/60 hover:-translate-y-1 transition-[border-color,transform] duration-500"
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    {c.category}
                  </span>
                  {c.upfront_fee ? (
                    <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-destructive" data-testid={`company-fee-${i}`}>
                      <Warning size={16} weight="fill" /> Upfront fee
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary" data-testid={`company-fee-${i}`}>
                      <HandCoins size={16} weight="fill" /> Low upfront cost
                    </span>
                  )}
                </div>
                <h3 className="font-serif text-2xl text-primary tracking-tight mt-4">{c.label}</h3>
                <p className="mt-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-accent" data-testid={`company-locked-${i}`}>
                  <LockSimple size={13} weight="bold" /> Name shared with members
                </p>
                <div className="flex items-center gap-3 mt-4">
                  {c.stars && <Stars rating={c.stars} />}
                  <span className="text-sm font-bold text-foreground/70" data-testid={`company-rating-${i}`}>{c.rating_detail}</span>
                </div>
                <dl className="mt-6 space-y-4 text-sm flex-1">
                  <div>
                    <dt className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Track record</dt>
                    <dd className="mt-1 font-semibold text-foreground/80">{c.record}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Fee model</dt>
                    <dd className="mt-1 font-semibold text-foreground/80">{c.fee_model}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Red flags</dt>
                    <dd className="mt-1">
                      {c.red_flags.length === 0 ? (
                        <span className="text-primary font-semibold">None on record</span>
                      ) : (
                        <ul className="space-y-1.5">
                          {c.red_flags.map((f, j) => (
                            <li key={j} className="flex gap-2 text-destructive font-medium">
                              <span className="mt-[8px] h-1 w-1 shrink-0 rounded-full bg-destructive" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      )}
                    </dd>
                  </div>
                </dl>
                <p className="mt-6 pt-5 border-t border-border text-xs font-semibold text-foreground/50">
                  Best for: <span className="text-foreground/75">{c.best_for}</span>
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-t border-border pt-8">
          <p className="text-xs text-foreground/50 max-w-2xl leading-relaxed" data-testid="directory-disclaimer">
            Real companies and real public records — BBB, Trustpilot and consumer complaint boards, checked {companies?.[0]?.as_of || "August 2026"}.
            Names are shared with members inside the private client portal.
            The Timeshare Edit accepts no referral fees, commissions or compensation from any company — ever.
            Ratings change; always re-verify before engaging anyone.
          </p>
          <button
            data-testid="directory-cta-consult"
            onClick={() => scrollToId("consult")}
            className="group flex items-center gap-2 text-sm font-semibold text-primary border-b-2 border-accent pb-1 hover:text-accent transition-colors duration-300 shrink-0"
          >
            Ask us about a company
            <ArrowUpRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </Reveal>
      </div>
    </section>
  );
}
